import { Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CardStatus,
  MissionRoleType,
  OperationStatus,
  RequestStatus,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { selectCardSummaryByUnitId } from 'src/store/entityStores/card-selectors';
import { selectCardSummaryEntityByCardNumber } from 'src/store/entityStores/card-summary-selectors';
import {
  selectNextExternalActionInQueueByCardId,
  selectPendingExternalActionsByEntityId,
} from 'src/store/entityStores/external-action-selectors';
import {
  CardSummaryProps,
  CardSummaryViewModelBase,
  selectAllCardSummaryEntities,
} from 'src/store/generated/card-summary-record.generated';
import { CardSummaryEntityFetchService } from 'src/store/generated/client-sync.generated';
import { ExternalActionRecord } from 'src/store/generated/external-action-record.generated';
import { RequestSummaryProps } from 'src/store/generated/request-summary-record.generated';
import { selectWebOrderLogEntitiesByEntityId } from 'src/store/generated/web-order-log-record.generated';
import { undefinedOrMap } from 'src/util/rxjs/operators';
import { humanize } from 'src/util/text';

export class CardSummaryViewModel extends CardSummaryViewModelBase {
  public pendingExternalActions$: Observable<ExternalActionRecord[]>;
  public nextExternalActionInQueue$: Observable<ExternalActionRecord | undefined>;
  public hasUnacknowledgedWebOrders$: Observable<boolean>;
  constructor(props: CardSummaryProps, private _store: Store<AppState>) {
    super(props);

    this.pendingExternalActions$ = this._store
      .select(selectPendingExternalActionsByEntityId(this.id))
      .pipe(map((a) => a.valueSeq().toArray()));

    this.nextExternalActionInQueue$ = this._store.select(
      selectNextExternalActionInQueueByCardId(this.id),
    );

    this.hasUnacknowledgedWebOrders$ = this._store
      .select(selectWebOrderLogEntitiesByEntityId(this.id))
      .pipe(
        map((logs) => {
          let hasNotAcknowledged = false;
          logs.forEach((log) => {
            if (log.acknowledgedById == null) {
              hasNotAcknowledged = true;
            }
          });
          return hasNotAcknowledged;
        }),
      );
  }

  get isReadOnly() {
    return this.isClosed;
  }

  get tripId() {
    return this.operation != null ? this.operation.tripId : undefined;
  }

  get cardNumberDisplay(): string {
    const operation = this.operation;
    let onStandby = false;

    if (operation != null) {
      onStandby = operation.status === OperationStatus.OnStandby;
    }

    return `${this.cardNumber} ${onStandby ? '*' : ''}`.trim();
  }

  get isWebOrder() {
    return this.apiWebhookSubscriptionId != null;
  }

  get hasCrewAccepted() {
    if (this.operation == null) {
      return false;
    }

    return this.operation.status === OperationStatus.CrewAccepted;
  }

  get isAbortedOrCancelled() {
    if (this.operation == null) {
      return false;
    }

    if (this.operation.status === OperationStatus.Aborted) {
      return true;
    }

    if (this.operation.status === OperationStatus.Cancelled) {
      return true;
    }

    return false;
  }

  get isWebOrderWaiting() {
    if (this.isWebOrder !== true) {
      return false;
    }

    return this.webOrderAcknowledgedTime == null;
  }

  get isActionRequired$(): Observable<boolean> {
    return this.pendingExternalActions$.pipe(map((e) => e.length > 0));
  }

  get isSisterCompanyReferralActive() {
    return (
      (this.request?.isSisterCompanyReferral ?? false) &&
      this.status === CardStatus.AwaitingAssignment
    );
  }

  private get _requestStatusHumanized() {
    return humanize(this.request?.status ?? 'Unknown');
  }

  public requestStatusDisplay() {
    const request: RequestSummaryProps | null = this.request;

    if (request == null) {
      return 'Unknown';
    }

    if (this.isWebOrderWaiting === true) {
      return this.request?.requester?.missionRole === MissionRoleType.TeamFlight
        ? 'Web Order - Team Flight'
        : this.request?.requester?.missionRole === MissionRoleType.MassCasualty
        ? 'Web Order - Mass Casualty'
        : 'Web Order';
    }

    if (this.isSisterCompanyReferralActive) {
      return `${this._requestStatusHumanized} - Referral`;
    }

    if (request.isStandbyRequest === true && this.isWebOrder === true) {
      return `${this._requestStatusHumanized} - STANDBY`;
    }

    if (
      request.status === RequestStatus.AssignmentDelayed &&
      request.assignmentDelayedReason != null
    ) {
      return `${request.assignmentDelayedReason} - DELAY`;
    }

    return this._requestStatusHumanized;
  }
}

@Injectable({ providedIn: 'root' })
export class CardSummaryViewModelService {
  constructor(
    private readonly _cardSummaryFetchService: CardSummaryEntityFetchService,
    private _store: Store<AppState>,
  ) {}

  public getById(id: string): Observable<CardSummaryViewModel | undefined> {
    return this._cardSummaryFetchService
      .getById(id)
      .pipe(undefinedOrMap((card) => new CardSummaryViewModel(card, this._store)));
  }

  public getByUnitId(unitId: string): Observable<CardSummaryViewModel | undefined> {
    return this._store
      .select(selectCardSummaryByUnitId(unitId))
      .pipe(undefinedOrMap((card) => new CardSummaryViewModel(card, this._store)));
  }

  public getByCardNumber(cardNumber: string): Observable<CardSummaryViewModel | undefined> {
    return this._store
      .select(selectCardSummaryEntityByCardNumber(cardNumber))
      .pipe(undefinedOrMap((card) => new CardSummaryViewModel(card, this._store)));
  }
}

export function selectCardSummaryByTripId(tripId: string) {
  return createSelector(selectAllCardSummaryEntities, (cardSummaries) => {
    return cardSummaries.find((c) => c.operation != null && c.operation.tripId === tripId);
  });
}
